// src/store/slices/scenariosSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import * as con from "../../GlobalConstants";
import { extractErrorMessage } from "../../utils/errorFunctions";
import { createError } from "../../templates/notifications/Notifications";


// =========================
// Helpers
// =========================

const mapArrayToObject = (arr) =>
  Object.fromEntries(arr.map((x) => [x[con.ID], x]));

// =========================
// THUNK
// =========================

export const fetchScenarios = createAsyncThunk(
  "scenarios/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      // ================= HISTORIC =================
      const historicRes = await axios.get(
        con.rest_base_url + "/scenarios/api/historic/"
      );

      let historicScenarios = historicRes.data.map((sce) => {
        const values = Array(13)
          .fill(1)
          .map((_, i) => sce[`month_${i}`]);

        return {
          [con.ID]: sce.scenario_id,
          [con.NAME]: sce.name,
          [con.TYPE]: con.MONTHLY_VALUES,
          [con.PARAMETERS]: {
            [con.MONTHLY_VALUES]: values,
            fixed: false,
            [con.SPOT]: false,
          },
        };
      });

      historicScenarios = mapArrayToObject(historicScenarios);

      // ================= BOR =================
      const borRes = await axios.get(
        con.rest_base_url + "/scenarios/api/bank_republic_current/"
      );

      const borData = borRes.data;

      const days = [6 * con.DAYS_IN_MONTH, 12 * con.DAYS_IN_MONTH];

      const borScenarios = {
        [con.SCENARIO_BOR_MIN]: {
          [con.ID]: con.SCENARIO_BOR_MIN,
          [con.NAME]: con.SCENARIO_BOR_MIN_NAME,
          [con.TYPE]: con.VALUES,
          [con.PARAMETERS]: {
            days,
            [con.VALUES]: [borData.min_6, borData.min_12],
            interpolate: true,
          },
        },
        [con.SCENARIO_BOR_AVG]: {
          [con.ID]: con.SCENARIO_BOR_AVG,
          [con.NAME]: con.SCENARIO_BOR_AVG_NAME,
          [con.TYPE]: con.VALUES,
          [con.PARAMETERS]: {
            days,
            [con.VALUES]: [borData.min_6, borData.min_12],
            interpolate: true,
          },
        },
        [con.SCENARIO_BOR_MAX]: {
          [con.ID]: con.SCENARIO_BOR_MAX,
          [con.NAME]: con.SCENARIO_BOR_MAX_NAME,
          [con.TYPE]: con.VALUES,
          [con.PARAMETERS]: {
            days,
            [con.VALUES]: [borData.max_6, borData.max_12],
            interpolate: true,
          },
        },
      };

      // ================= VINCO =================
      const vincoRes = await axios.get(
        con.rest_base_url + "/scenarios/api/vinco_current/"
      );

      const vincoData = vincoRes.data;

      const vincoValues = Array(13)
        .fill(1)
        .map((_, i) => vincoData[`month_${i}`]);

      const vincoScenario = {
        [con.ID]: con.SCENARIO_VINCO,
        [con.NAME]: con.SCENARIO_VINCO_NAME,
        [con.TYPE]: con.MONTHLY_VALUES,
        [con.PARAMETERS]: {
          [con.MONTHLY_VALUES]: vincoValues,
          fixed: false,
          [con.SPOT]: true,
        },
      };

      // ================= MERGE ALL =================
      return {
        ...historicScenarios,
        ...borScenarios,
        [vincoScenario[con.ID]]: vincoScenario,
      };
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err,"ERROR_FETCHING_SCENARIOS"));
    }
  }
);

// =========================
// INITIAL STATE
// =========================

const buildBootstrapVarScenario = (direction) => ({
  [con.ID]:
    direction === 1
      ? con.SCENARIO_VAR_UP
      : con.SCENARIO_VAR_DOWN,
  [con.NAME]:
    direction === 1
      ? con.SCENARIO_VAR_UP_NAME
      : con.SCENARIO_VAR_DOWN_NAME,
  [con.TYPE]: "var",
  [con.PARAMETERS]: {
    direction,
  },
});

const initialState = {
  byId: {
    [con.SCENARIO_VAR_UP]: buildBootstrapVarScenario(1),
    [con.SCENARIO_VAR_DOWN]: buildBootstrapVarScenario(-1),
  },
  status: con.NOT_STARTED,
};


// =========================
// SLICE
// =========================

const scenariosSlice = createSlice({
  name: con.REDUCER_SCENARIOS,
  initialState,
  reducers: {
    replaceScenarios: (state, action) => {
      state.byId = action.payload;
    },

    setScenario: (state, action) => {
      const scenario = action.payload;
      state.byId[scenario[con.ID]] = scenario;
    },

    resetScenarios: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchScenarios.pending, (state) => {
        state.status = con.LOADING;
      })
      .addCase(fetchScenarios.fulfilled, (state, action) => {
        state.status = con.OK;

        state.byId = {
          ...action.payload,
          ...state.byId, 
        };
      })
      .addCase(fetchScenarios.rejected, (state, action) => {
        state.status = con.ERROR;
        createError(action.payload);
      });
  },
});

export const {
  replaceScenarios,
  setScenario,
  resetScenarios,
} = scenariosSlice.actions;

export default scenariosSlice.reducer;